import { Button, Col, Input, Row, Table, Pagination as Paging } from 'antd';
import React, { SyntheticEvent, useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/layout/PageHeader';
import { deleteInference } from '../../../lib/api/aws-api';
import { useInterval } from '../../../lib/useInterval';
import { isDocumentClassification, isMultiflow, userIsAdmin } from '../../../lib/util';
import { anyRecentProcessingInference } from '../../../lib/util/processing';
import { getAlgorithmDemoAction, getAlgorithmValue } from '../../../lib/util/algorithm';
import { InferenceFile, Pagination } from '../../../types';
import { getDemoClassificationOverviewTableColumns } from './DemoClassificationOverviewTable';
import { getDemoOverviewTableColumns } from './DemoOverviewTable';
import DocumentUpload from './DocumentUpload';
import { useGetAlgorithm, useGetInferenceList } from '../../../lib/hooks';
import { algorithmMenuDescriptions } from '../../../lib/status-templates';
import { ErrorText } from '../../../components/text/ErrorText';
import { ManageModels } from './ManageModels';
import { useAuthenticationContext } from '../../authentication/AuthenticationContext';

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

const DemoOverview = () => {
  const { currentUser } = useAuthenticationContext();

  const { algorithmId, version } = useParams() as { algorithmId: string; version?: string };

  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [manageModalVisible, setManageModalVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const { algorithm } = useGetAlgorithm(algorithmId);

  const { inferenceList, itemsCount, loading, refetch, error } = useGetInferenceList(
    algorithmId,
    paginationObject,
    filter
  );

  useEffect(() => {
    if (version) {
      setModalVisible(true);
    }
  }, [version]);

  useInterval(async () => {
    if (!algorithm) return;

    try {
      if (anyRecentProcessingInference(inferenceList)) {
        refetch(false);
      }
    } catch (e) {}
  }, 2000);

  const onClickUrl = useCallback(
    (record: InferenceFile) => {
      return `/demo/${algorithmId}/${record.id}/result`;
    },
    [algorithmId]
  );

  const onDelete = async (record: InferenceFile) => {
    if (!algorithm) return;
    await deleteInference(record.id);
    refetch(true);
  };

  const onFilterChange = (value: SyntheticEvent<HTMLInputElement>) => {
    setFilter(value.currentTarget.value);
  };

  const onDocumentUploaded = () => {
    if (!algorithm) return Promise.resolve();
    refetch(true);
  };

  const isClassification = isDocumentClassification(algorithm);
  const isMultiflowAlgorithm = isMultiflow(algorithm);

  useEffect(() => {
    setPaginationObject(defaultPaging);
  }, [algorithmId]);

  return (
    <>
      <PageHeader
        container="Demo"
        header={(algorithm && getAlgorithmValue(algorithm.name)) || null}
        description={
          algorithm && (algorithmMenuDescriptions.demo[algorithm.type] || algorithmMenuDescriptions.demo.other)
        }
      />
      <Row className="items-center my-3">
        <Col span={12}>
          <Input placeholder="Filter" onChange={onFilterChange} allowClear={true} />
        </Col>
        <Col span={12} className="flex justify-end">
          <Button className="mr-3" onClick={() => setModalVisible(true)} type="primary" size={'large'}>
            {algorithm && getAlgorithmDemoAction(algorithm.name)}
          </Button>

          {userIsAdmin(currentUser) ? (
            <Button onClick={() => setManageModalVisible(true)} type="primary" size={'large'}>
              Manage Models
            </Button>
          ) : null}
        </Col>
      </Row>
      <Row>
        <Col span={24}>
          {error ? (
            <ErrorText>Something went wrong. We could not find the documents you were looking for.</ErrorText>
          ) : (
            <>
              <Table
                className="ant-table-toolbox"
                columns={
                  isClassification
                    ? getDemoClassificationOverviewTableColumns(onClickUrl, onDelete)
                    : getDemoOverviewTableColumns(onClickUrl, onDelete, isMultiflowAlgorithm)
                }
                rowKey={record => record.id}
                dataSource={inferenceList}
                loading={loading}
                pagination={false}
              />
              <div className="flex justify-end my-4">
                <Col className="mr-4">{itemsCount} Documents</Col>
                <Paging
                  showSizeChanger={false}
                  defaultCurrent={1}
                  current={paginationObject.page}
                  pageSize={defaultPaging.pageSize}
                  total={itemsCount}
                  onChange={page => setPaginationObject({ ...defaultPaging, page: page })}
                />
              </div>
            </>
          )}
        </Col>
      </Row>
      {algorithm && modalVisible && (
        <DocumentUpload
          title={(algorithm && getAlgorithmDemoAction(algorithm.name)) || ''}
          visible={modalVisible}
          algorithm={algorithm}
          version={version}
          onDocumentUploaded={onDocumentUploaded}
          onClose={() => setModalVisible(false)}
        />
      )}
      {algorithm && manageModalVisible ? (
        <ManageModels
          visible={manageModalVisible}
          algorithm={algorithm}
          algorithmId={algorithm.id}
          onClose={() => setManageModalVisible(false)}
        />
      ) : null}
    </>
  );
};

export default DemoOverview;
